import { showToast, renderPagination } from './utils.js';
import {
  renderUsageLimitState,
  renderUsageLimitTableView,
} from './presentation/cost-calculation-usage.js';
import { createUsageLimitDialogView } from './presentation/cost-calculation-summary.js';

const USAGE_LIMIT_RESOURCES = Object.freeze([
  { key: 'linkfox', label: 'LinkFox 算力', unit: '点' },
  { key: 'llm', label: 'LLM Token', unit: 'token' },
  { key: 'market', label: '市场工具', unit: '次' },
]);

const EMPTY_USAGE_LIMIT_PAYLOAD = Object.freeze({ users: [], total: 0, page: 1, size: 20 });

/**
 * 管理端费用配置页的用户限额交互控制器。
 *
 * 列表、搜索、批量选择和保存限额共用一份局部状态；成本配置控制器只负责
 * 加载页面数据与协调其它费用展示，不再持有这段交互状态。
 */
export function createUsageLimitsController({ facade, formatCurrency, formatCount, getRoot }) {
  let payload = { ...EMPTY_USAGE_LIMIT_PAYLOAD };
  const selectedUserIds = new Set();

  function replacePayload(nextPayload) {
    payload = nextPayload && typeof nextPayload === 'object'
      ? nextPayload
      : { ...EMPTY_USAGE_LIMIT_PAYLOAD };
  }

  function clearSelection() {
    selectedUserIds.clear();
  }

  function getPayload() {
    return payload;
  }

  function getRootElement() {
    return typeof getRoot === 'function' ? getRoot() : null;
  }

  function renderTable(root = getRootElement()) {
    const userBlock = root?.querySelector('#cost-calc-estimate-tables');
    if (!userBlock) return;

    const users = Array.isArray(payload?.users) ? payload.users : [];
    if (!users.length) {
      renderUsageLimitTableView(userBlock, {
        users,
        resources: USAGE_LIMIT_RESOURCES,
        selectedUserIds,
        formatCurrency,
        formatCount,
        statusLabel: usageLimitStatusLabel,
      });
      renderPagination('cost-limit-pagination', 0, 1, payload?.size || 20, loadUsers);
      return;
    }

    const selectedOnPage = users.filter((user) => selectedUserIds.has(Number(user.userId))).length;
    const allSelected = users.length > 0 && selectedOnPage === users.length;

    renderUsageLimitTableView(userBlock, {
      users,
      allSelected,
      resources: USAGE_LIMIT_RESOURCES,
      selectedUserIds,
      formatCurrency,
      formatCount,
      statusLabel: usageLimitStatusLabel,
    });
    const caption = document.getElementById('cost-calc-estimate-caption');
    if (caption) caption.textContent = `限额状态按用户当前周期用量计算；已勾选 ${selectedUserIds.size} 个用户`;

    userBlock.querySelector('#cost-limit-select-all')?.addEventListener('change', (event) => {
      users.forEach((user) => {
        const userId = Number(user.userId);
        if (event.target.checked) selectedUserIds.add(userId);
        else selectedUserIds.delete(userId);
      });
      renderTable(root);
    });
    userBlock.querySelectorAll('.cost-limit-user-check').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const userId = Number(checkbox.dataset.userId);
        if (checkbox.checked) selectedUserIds.add(userId);
        else selectedUserIds.delete(userId);
        renderTable(root);
      });
    });
    userBlock.querySelectorAll('.cost-limit-edit-btn').forEach((button) => {
      button.addEventListener('click', () => {
        const user = users.find((item) => Number(item.userId) === Number(button.dataset.userId));
        if (!user) return;
        openDialog({
          mode: 'single',
          userIds: [Number(user.userId)],
          user,
          title: `设置 ${user.name || user.email || `用户 #${user.userId}`}`,
        });
      });
    });
    renderPagination(
      'cost-limit-pagination',
      Number(payload?.total || 0),
      Number(payload?.page || 1),
      Number(payload?.size || 20),
      loadUsers,
    );
  }

  function bindEvents(root = getRootElement()) {
    if (!root) return;
    root.querySelector('#cost-limit-search-btn')?.addEventListener('click', () => loadUsers(1));
    root.querySelector('#cost-limit-search')?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') loadUsers(1);
    });
    root.querySelector('#cost-limit-batch-btn')?.addEventListener('click', () => {
      if (selectedUserIds.size === 0) {
        showToast('请先勾选要设置的用户', 'warning');
        return;
      }
      openDialog({
        mode: 'batch',
        userIds: Array.from(selectedUserIds),
        title: `批量设置 ${selectedUserIds.size} 个用户`,
      });
    });
  }

  function usageLimitStatusLabel(status) {
    if (status === 'blocked') return '已达上限';
    if (status === 'watching') return '观测中';
    if (status === 'normal') return '正常';
    return '未限制';
  }

  async function loadUsers(page = 1) {
    const root = getRootElement();
    const userBlock = root?.querySelector('#cost-calc-estimate-tables');
    const search = document.getElementById('cost-limit-search')?.value?.trim() || '';
    if (userBlock) renderUsageLimitState(userBlock, '正在加载用户限额...');
    try {
      replacePayload(await facade.listUsageLimits({ page, size: payload?.size || 20, search }));
      renderTable(root);
    } catch (error) {
      console.error('loadUsageLimitUsers failed', error);
      showToast(error.message || '加载用户限额失败', 'error');
      if (userBlock) renderUsageLimitState(userBlock, error.message || '加载失败');
    }
  }

  function openDialog({ mode, userIds, user = null, title }) {
    const existing = document.getElementById('cost-limit-dialog');
    if (existing) existing.remove();
    const dialog = createUsageLimitDialogView({
      title: title || '设置用户限额',
      resources: USAGE_LIMIT_RESOURCES,
      seedLimits: user?.limits || {},
    });
    dialog.id = 'cost-limit-dialog';
    dialog.className = 'modal-overlay';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target?.dataset?.action === 'cancel') dialog.remove();
    });
    dialog.querySelector('[data-action="save"]')?.addEventListener('click', () => {
      saveDialog({ mode, userIds, dialog });
    });
    document.body.appendChild(dialog);
  }

  function readDialog(dialog) {
    const limits = {};
    dialog.querySelectorAll('[data-resource]').forEach((section) => {
      const resourceType = section.dataset.resource;
      limits[resourceType] = {
        enabled: section.querySelector('[data-field="enabled"]')?.checked || false,
        periodType: section.querySelector('[data-field="periodType"]')?.value || 'daily',
        limitUnits: Number(section.querySelector('[data-field="limitUnits"]')?.value || 0),
      };
    });
    return limits;
  }

  async function saveDialog({ mode, userIds, dialog }) {
    const saveButton = dialog.querySelector('[data-action="save"]');
    if (saveButton) saveButton.disabled = true;
    try {
      const limits = readDialog(dialog);
      await facade.saveUsageLimits({ mode, userIds, limits });
      showToast('用户限额已保存', 'success');
      dialog.remove();
      await loadUsers(payload?.page || 1);
    } catch (error) {
      console.error('saveUsageLimitDialog failed', error);
      showToast(error.message || '保存限额失败', 'error');
    } finally {
      if (saveButton) saveButton.disabled = false;
    }
  }

  return {
    bindEvents,
    clearSelection,
    getPayload,
    loadUsers,
    renderTable,
    replacePayload,
  };
}
